import { memo, useCallback, useEffect, useState } from 'react';
import classnames from 'classnames';
import { useRecoilState, captionsEnabledState } from '../../../../store';
import { dLog } from '@/_helpers/renderDebug';

let lastTimestamp = 0;

const SoundControls = ({ audio, onClose }: any) => {
  dLog('SoundControls');
  const [soundTime, setSoundTime] = useState(0);
  const [isShowVolumeRange, toggleVolumeRange] = useState(false);
  const [captionsEnabled, setCaptionsEnabled] = useRecoilState(captionsEnabledState);

  useEffect(() => {
    const timeupdate = () => {
      if (!audio.currentTime || !audio.duration) {
        return;
      }
      setSoundTime((audio.currentTime / audio.duration) * 100);
    };
    audio.addEventListener('timeupdate', timeupdate);
    return () => {
      audio.removeEventListener('timeupdate', timeupdate);
    };
  }, [audio]);

  const [volume, setVolume] = useState(1);
  useEffect(() => {
    if (audio) {
      // eslint-disable-next-line no-param-reassign
      audio.volume = volume;
    }
    return () => {
      if (audio) audio?.closeReceivers?.dispatchEvent(new Event('close'));
    };
  }, [audio, volume]);

  const getSoundTime = useCallback(() => {
    const ONE_MINUTE = 60;
    if (audio) {
      const duration = !Number.isNaN(audio.duration) ? audio.duration : 0;
      const hour = parseInt(String(duration / ONE_MINUTE), 10);
      const minutes: number = Math.round(duration % ONE_MINUTE);
      return `${hour}:${String(minutes).length === 1 ? `0${minutes}` : minutes}`;
    }
    return '00:00';
  }, [audio]);

  const setCurrentTime = useCallback(
    (time, data) => {
      if (data.timeStamp - lastTimestamp > 300) {
        lastTimestamp = data.timeStamp;
        const { duration } = audio;
        if (audio && !Number.isNaN(duration)) {
          // eslint-disable-next-line no-param-reassign
          audio.currentTime = (time * duration) / 100;
        }
      }
    },
    [audio],
  );

  const setTogglePlay = useCallback(() => {
    if (!audio?.paused) {
      audio.pause();
    } else {
      audio.play();
    }
  }, [audio]);

  const handleClose = useCallback(
    (mode: 'close' | 'hide') => () => {
      if (mode === 'close') {
        audio?.pause();
        audio.src = null;
        if (audio?.closeReceivers) {
          audio?.closeReceivers?.dispatchEvent(new Event('close'));
        }
      }
      if (onClose && typeof onClose === 'function') {
        onClose();
      }
    },
    [audio, onClose],
  );

  return (
    <div className="sounds">
      <div className="container-controls">
        <i className="fas fa-minus" onClick={handleClose('hide')} />
        <i className="fas fa-times" onClick={handleClose('close')} />
      </div>
      <div className="sound-range">
        <input type="range" step="0.1" min="0" max="100" value={soundTime} onChange={(data) => setCurrentTime(parseFloat(data.target.value), data)} />
      </div>
      <div className="sound-controls">
        <div className="left-side">
          <div className="play">
            <i className={classnames('fas', !audio?.paused ? 'fa-pause' : 'fa-play')} onClick={setTogglePlay} />
          </div>
          {audio?.textTracks?.length ? (
            <div>
              <i className={classnames(captionsEnabled ? 'fas fa-closed-captioning' : 'fal fa-closed-captioning')} onClick={() => setCaptionsEnabled(!captionsEnabled)} />
            </div>
          ) : null}
          <div className="volume">
            <i className="fas fa-volume-up" onClick={() => toggleVolumeRange(!isShowVolumeRange)} />
            {isShowVolumeRange && <input type="range" step="0.1" min="0" max="1" value={volume} onChange={(data) => setVolume(parseFloat(data.target.value))} />}
          </div>
        </div>
        <div className="right-side">
          <div className="time">{getSoundTime()}</div>
        </div>
      </div>
    </div>
  );
};

export default memo(SoundControls);
